/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useState, useRef, useCallback } from "react";
import EverTrue from "app";
import useUserAuthentication from "./useUserAuthentication";
import PropTypes from "prop-types";

export const RecorderContext = createContext();
const container = {
  textAlign: "center",
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
  fontFamily: "'Lato', sans-serif",
};

const textStyle = {
  fontSize: "16px",
  lineHeight: "1.5",
  color: "#333",
  margin: "20px 0",
  textAlign: "center",
  width: "100%",
  padding: "10px 20px",
  display: "inline-block",
};

const linkStyle = {
  color: "#3367F2",
  textDecoration: "underline",
};

const button = {
  backgroundColor: "#3367F2",
  color: "#fff",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "10px 20px",
  borderRadius: "100px",
  textDecoration: "none",
  fontSize: "16px",
  fontWeight: "400",
  width: "fit-content",
  margin: "20px auto",
};

const imgStyle = {
  display: "block",
  margin: "0 auto",
  maxWidth: "100%",
};
export const RecorderProvider = ({ children }) => {
  const { user, clientId = "" } = useUserAuthentication();
  const [state, setState] = useState({
    users: [],
    isInitialOpen: true,
    loading: false,
    error: null,
    signalVideos: [],
    webcamStream: null,
    webcamEnabled: false,
    isRecording: false,
    isReviewing: false,
    isCountingDown: false,
    isProcessing: false,
    isUploading: false,
    isToastOpen: false,
    isSelectedLibraryVideosMoreThanOne: false,
    isSelectedLibraryVideosAreNone: true,
    countdown: 3,
    timestamp: 0,
    videoUrl: null,
    recordedVideoSrc: null,
    isModalOpen: false,
    savedVideos: [],
    selectedVideoId: {},
    defaultSettings: {},
  });

  const videoRef = useRef(null);
  const reviewVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const screenVideoRef = useRef(null);
  const chunksRef = useRef([]);
  const [lastBlob, setLastBlob] = useState([]);
  const timestampInterval = useRef(null);
  const [resolutions, setResolutions] = useState([]);
  const [selectedResolution, setSelectedResolution] = useState("");
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState("");
  const [selectedVideoDevice, setSelectedVideoDevice] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [color, setColor] = useState("#3367F2");
  const [trackColor, setTrackColor] = useState("#FFFFFF");
  const abortControllerRef = useRef(null);
  const [client_id, setClientId] = useState();
  const [client_secret, setClientSecret] = useState();
  const [selectedLibraryVideo, setSelectedLibraryVideo] = useState(null);

  const handleToastClose = () => {
    updateState({ isToastOpen: false });
  };
  const handleCopyToast = async () => {
    try {
      updateState({ isToastOpen: true });
      // Set a timeout to close the toast after 5 seconds
      setTimeout(handleToastClose, 5000);
    } catch (err) {
      EverTrue.Alert.error("Failed to copy text");
    }
  };
  const handleColorChange = (event) => {
    const newColor = event.target.value;
    setColor(newColor);
  };
  const handleLibraryVideo = (video) => {
    setSelectedVideo(video);
  };
  const handleTrackColorChange = (event) => {
    const newColor = event.target.value;
    setTrackColor(newColor);
  };

  async function uploadVideoFromFile(video, fileName, duration, personalVideoParams = {}) {
    updateState({ isProcessing: true });

    try {
      const formData = new FormData();
      formData.append("file", video);
      formData.append("video_length", Math.round(duration).toString());
      formData.append("personalVideoParams", JSON.stringify(personalVideoParams));

      const response = await fetch("https://thankview.com/api/signal/personal/upload", {
        method: "POST",
        headers: {
          "Signal-Client-Key": clientId && clientId.data.client_id,
          "Signal-Client-Secret": clientId && clientId.data.client_secret,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Failed to upload video: ${response.status} ${response.statusText}. Server response: ${JSON.stringify(
            errorData
          )}`
        );
      }

      const data = await response.json();
      setLastBlob([]);
      setSelectedVideo(data);

      // Wait for the video to load before updating state
      if (reviewVideoRef.current && reviewVideoRef.current.getVideo()) {
        const videoElement = reviewVideoRef.current.getVideo();
        videoElement.src = data.data.libraryVideo.video_path;
        await new Promise((resolve, reject) => {
          videoElement.onloadedmetadata = () => {
            updateState({
              videoUrl: data.data.libraryVideo.video_path,
              isProcessing: false,
              isReviewing: true,
              videoDuration: reviewVideoRef.current.getDuration(),
            });
            resolve();
          };
          videoElement.onerror = (e) => {
            updateState({ isUploading: false, uploadError: "Error loading video" });
            reject(new Error("Error loading video"));
          };
        });
        videoElement.load();
        saveVideoToState(data.data.libraryVideo.video_path);
      } else {
        updateState({ isProcessing: false, uploadError: "Video player not ready" });
      }
      return data;
    } catch (error) {
      EverTrue.Alert.error(`Upload failed: ${error.message}`);
      throw error;
    } finally {
      updateState({ isProcessing: false });
    }
  }
  async function reviewVideo(video) {
    return new Promise((resolve, reject) => {
      try {
        const url = video.data.libraryVideo.video_path;
        updateState({
          videoUrl: url,
          isUploading: false,
          isReviewing: true,
          isProcessing: false,
        });

        if (!reviewVideoRef.current) {
          throw new Error("Video ref is not available");
        }

        const videoElement = reviewVideoRef.current.getVideo();
        if (!videoElement) {
          throw new Error("Unable to get video element from ref");
        }

        videoElement.src = url;

        const onLoadedMetadata = () => {
          updateState({
            videoUrl: url,
            isUploading: false,
            isReviewing: true,
            videoDuration: videoElement.duration,
          });
          saveVideoToState(url);
          resolve();
        };

        const onError = (e) => {
          EverTrue.Alert.error(`Error loading video: for  ${e}`);
          updateState({ isUploading: false, uploadError: "Error loading video" });
          reject(new Error("Error loading video"));
        };

        const onLoadedData = () => {
          EverTrue.Alert.success("Video data has loaded");
        };

        videoElement.addEventListener("loadedmetadata", onLoadedMetadata);
        videoElement.addEventListener("error", onError);
        videoElement.addEventListener("loadeddata", onLoadedData);

        const timeoutId = setTimeout(() => {
          reject(new Error("Video loading timed out"));
        }, 30000); // 30 seconds timeout

        videoElement.load();

        const cleanup = () => {
          videoElement.removeEventListener("loadedmetadata", onLoadedMetadata);
          videoElement.removeEventListener("error", onError);
          videoElement.removeEventListener("loadeddata", onLoadedData);
          clearTimeout(timeoutId);
        };

        resolve = wrap(resolve, cleanup);
        reject = wrap(reject, cleanup);
      } catch (error) {
        console.error('Error in reviewing video');
        updateState({ isUploading: false, uploadError: error.message });
        reject(error);
      }
    });
  }

  function wrap(fn, cleanup) {
    return (...args) => {
      cleanup();
      return fn(...args);
    };
  }
  async function getAudioAndVideoDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioDevices = devices.filter((device) => device.kind === "audioinput");
      const videoDevices = devices.filter((device) => device.kind === "videoinput");

      setAudioDevices(audioDevices);
      setVideoDevices(videoDevices);

      if (audioDevices.length > 0) {
        setSelectedAudioDevice(audioDevices[0].deviceId);
      }

      if (videoDevices.length > 0) {
        setSelectedVideoDevice(videoDevices[0].deviceId);
      }
    } catch (error) {
      console.error("Error enumerating devices");
    }
  }
  async function getCameraResolutions() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const videoTrack = stream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities();

      if (capabilities.width && capabilities.height) {
        const resolutions = [];
        const standardResolutions = [
          { width: 1920, height: 1080 },
          { width: 1280, height: 720 },
          { width: 640, height: 480 },
        ];

        standardResolutions.forEach((res) => {
          if (res.width <= capabilities.width.max && res.height <= capabilities.height.max) {
            resolutions.push(res);
          }
        });

        setResolutions(resolutions);
        if (resolutions.length > 0) {
          setSelectedResolution(`${resolutions[0].width}x${resolutions[0].height}`);
        }
      } else {
        console.error("Camera does not provide width or height capabilities.");
      }
    } catch (error) {
      console.error("Error accessing media devices");
    }
  }

  const updateState = useCallback(
    (newState) => {
      setState((prevState) => {
        const updatedState = typeof newState === "function" ? newState(prevState) : newState;
        return { ...prevState, ...updatedState };
      });
    },
    [setState]
  );

  const resetRecorderState = () => {
    updateState({
      isRecording: false,
      isReviewing: false,
      webcamStream: null,
      recordedVideoSrc: null,
      isModalOpen: false,
      videoUrl: null,
      timestamp: 0,
      countdown: 3,
    });
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    chunksRef.current = [];
  };
  const startTimestamp = () => {
    updateState({ timestamp: 0 });
    if (timestampInterval.current) {
      clearInterval(timestampInterval.current);
    }

    setTimeout(() => {
      timestampInterval.current = setInterval(() => {
        updateState((prevState) => ({ timestamp: prevState.timestamp + 1 }));
      }, 1000);
    }, 0);
  };

  const stopTimestamp = () => {
    clearInterval(timestampInterval.current);
  };

  const resetTimestamp = () => {
    updateState({ timestamp: 0 });
  };

  const clearRecording = () => {
    updateState({ videoUrl: null });
    resetTimestamp();
    if (chunksRef.current) {
      chunksRef.current = [];
    }
    if (videoRef.current) {
      videoRef.current.src = "";
    }
  };

  const enableWebcam = async (customConstraints = {}) => {
    const fallbackConstraints = [
      {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: true,
      },
      {
        video: true,
        audio: true,
      },
      {
        video: true,
        audio: false,
      },
    ];

    const tryConstraints = async (constraints) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        await getAudioAndVideoDevices();
        await getCameraResolutions();
        updateState({ webcamStream: stream, webcamEnabled: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        return true;
      } catch (error) {
        if (error.name === "OverconstrainedError") {
          console.error("Camera doesn't support the required constraints");
        } else if (error.name === "NotAllowedError") {
          console.error("User denied camera/microphone access");
        } else if (error.name === "NotFoundError") {
          console.error("Camera or microphone not found");
        } else {
          console.error("Error accessing the webcam");
        }
      }
    };
    if (Object.keys(customConstraints).length > 0) {
      if (await tryConstraints(customConstraints)) return;
    }
    for (const constraints of fallbackConstraints) {
      if (await tryConstraints(constraints)) return;
    }
   console.error("Failed to enable webcam with all constraint options");
    updateState({ webcamEnabled: false });
  };

  const stopWebcam = () => {
    if (state.webcamStream) {
      state.webcamStream.getTracks().forEach((track) => {
        track.stop();
      });
      updateState({ webcamStream: null, webcamEnabled: false });
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const startRecording = async () => {
    if (!selectedResolution) {
      console.error("No resolution selected");
      return;
    }

    const [width, height] = selectedResolution.split("x").map(Number);
    const stopRecordingAfter5Mins = () => {
      state.timestamp = setTimeout(() => {
        stopRecording();
      }, 5 * 60 * 1000);
    };
    const getStreamWithFallback = async (initialWidth, initialHeight) => {
      const resolutionOptions = [
        { width: initialWidth, height: initialHeight },
        { width: 1280, height: 720 },
        { width: 640, height: 480 },
        {},
      ];

      for (const resolution of resolutionOptions) {
        try {
          const constraints = {
            audio: true,
            video: resolution.width
              ? {
                  width: { ideal: resolution.width },
                  height: { ideal: resolution.height },
                }
              : true,
          };

          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          // const videoTrack = stream.getVideoTracks()[0];
          // const actualSettings = videoTrack.getSettings();

          return stream;
        } catch (error) {
          if (error.name === "OverconstrainedError") {
            console.error(
              `Resolution ${resolution.width}x${resolution.height} not supported, trying next option`
            );
          } else {
            throw error;
          }
        }
      }
      console.error("Unable to get stream with any resolution option");
    };

    try {
      const stream = await getStreamWithFallback(width, height);
      updateState({ webcamStream: stream });

      const tryRecording = async (options = {}) => {
        try {
          mediaRecorderRef.current = new MediaRecorder(stream, options);
          chunksRef.current = [];
          mediaRecorderRef.current.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              chunksRef.current.push(event.data);
            }
          };
          mediaRecorderRef.current.start();
          startTimestamp();
          stopRecordingAfter5Mins();
          updateState({ isRecording: true });
          return true;
        } catch (error) {
          console.error("Error starting recording");
          return false;
        }
      };

      const fallbackOptions = [
        { mimeType: "video/webm;codecs=vp9,opus" },
        { mimeType: "video/webm;codecs=h264,opus" },
        { mimeType: "video/webm" },
        { mimeType: "video/mp4" },
      ];

      if (await tryRecording()) return;

      for (const options of fallbackOptions) {
        if (MediaRecorder.isTypeSupported(options.mimeType)) {
          console.error(`Trying fallback option: ${options.mimeType}`);
          if (await tryRecording(options)) return;
        }
      }

      console.error("Failed to start recording with all fallback options");
      updateState({ isRecording: false });
    } catch (error) {
      console.error(`Error in startRecording: ${error}`);
      updateState({ isRecording: false });
    }
  };

  const startCountdown = () => {
    updateState({ isCountingDown: true, countdown: 3 });
    const countdownInterval = setInterval(() => {
      updateState((prevState) => {
        if (prevState.countdown <= 1) {
          clearInterval(countdownInterval);
          startRecording();
          return { isCountingDown: false, countdown: 0 };
        }
        return { countdown: prevState.countdown - 1 };
      });
    }, 1000);
  };

  const stopRecording = () => {
    const finalize = () => {
      stopWebcam();
      stopTimestamp();
      updateState({ isRecording: false });
    };

    const processRecording = async (blob) => {
      try {
        const url = URL.createObjectURL(blob);
        const duration = await getBlobDuration(blob);
        updateState({ videoUrl: url, isReviewing: true, videoDuration: duration });
        if (reviewVideoRef.current) {
          reviewVideoRef.current.srcObject = null;
          reviewVideoRef.current.src = url;
          reviewVideoRef.current.load();
        }
        saveVideoToState(url);
        return { url, duration };
      } catch (error) {
        EverTrue.Alert.error(`Error processing recording ${error}`);
        throw error;
      }
    };

    const initiateUpload = async (blob, url, duration) => {
      try {
        await handleUpload(blob, "Untitled", duration);
        EverTrue.Alert.success("Upload completed successfully");
      } catch (error) {
        EverTrue.Alert.error(`Error during upload ${error}`);
        updateState({ uploadError: "Failed to upload video. Please try again." });
      }
    };

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.onstop = async () => {
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: mediaRecorderRef.current.mimeType });
          try {
            const { url, duration } = await processRecording(blob);
            finalize();
            initiateUpload(blob, url, duration);
          } catch (error) {
            console.error(`Error in processRecording ${error}`);
            finalize();
          }
        } else {
          finalize();
        }
      };

      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        console.error(`Error stopping MediaRecorder ${error}`);
        finalize();
      }
    } else {
      console.error("MediaRecorder not active or doesn't exist");
      finalize();
    }
  };

  const setModalOpen = (isOpen) => {
    if (isOpen) {
      resetRecorderState();
    }
  };

  const rerecord = () => {
    stopTimestamp();
    clearRecording();
    resetTimestamp();

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    mediaRecorderRef.current = null;

    updateState({ isReviewing: false, isRecording: false });
    enableWebcam();
  };

  const getBlobDuration = (blob) => {
    return new Promise((resolve, reject) => {
      const tempVideoEl = document.createElement("video");
      tempVideoEl.addEventListener("loadedmetadata", () => {
        if (tempVideoEl.duration === Infinity) {
          tempVideoEl.currentTime = Number.MAX_SAFE_INTEGER;
          tempVideoEl.ontimeupdate = () => {
            tempVideoEl.ontimeupdate = null;
            resolve(tempVideoEl.duration);
            tempVideoEl.remove();
          };
        } else {
          resolve(tempVideoEl.duration);
          tempVideoEl.remove();
        }
      });
      tempVideoEl.onerror = (event) => reject(event.target.error);
      tempVideoEl.src = URL.createObjectURL(blob);
    });
  };

  const saveVideo = useCallback(
    async (blob, fileName, duration, personalVideoParams = {}) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      updateState({ isProcessing: true });

      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      try {
        const videoFile = new File([blob], "blob.webm", { type: blob.type });
        const formData = new FormData();
        formData.append("file", videoFile);

        if (duration) {
          formData.append("video_length", Math.round(duration).toString());
        }

        formData.append("personalVideoParams", JSON.stringify(personalVideoParams));

        const uploadResponse = await fetch("https://thankview.com/api/signal/personal/upload", {
          method: "POST",
          headers: {
            "Signal-Client-Key": clientId && clientId.data.client_id,
            "Signal-Client-Secret": clientId && clientId.data.client_secret,
          },
          body: formData,
          signal: signal,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          EverTrue.Alert.error(
            `Failed to upload video: ${uploadResponse.status} ${
              uploadResponse.statusText
            }. Server response: ${JSON.stringify(errorData)}`
          );
        }

        const data = await uploadResponse.json();
        setLastBlob([]);
        setSelectedVideo(data);
        return data;
      } catch (error) {
        if (error.name === "AbortError") {
          EverTrue.Alert.error("Upload was aborted");
        } else {
          EverTrue.Alert.error(`Upload failed: ${error.message}`);
        }
        throw error;
      } finally {
        abortControllerRef.current = null;
        updateState({ isProcessing: false });
      }
    },
    [updateState, clientId]
  );

  const handleUpload = useCallback(
    async (blob, fileName, duration) => {
      setLastBlob((prevBlobs) => [...prevBlobs, { blob, fileName, duration }]);
      try {
        const result = await saveVideo(blob, fileName, duration);
        console.log("Upload completed:", result);
      } catch (error) {
        EverTrue.Alert.error("Upload failed");
      }
    },
    [saveVideo]
  );

  const handleRetry = useCallback(async () => {
    if (lastBlob.length === 0) {
      EverTrue.Alert.error("No previous upload to retry");
      return;
    }

    const retryUpload = async () => {
      const { blob, fileName, duration } = lastBlob[lastBlob.length - 1];
      try {
        await saveVideo(blob, fileName, duration);
        EverTrue.Alert.success("Retry upload completed successfully");
      } catch (error) {
        if (error.name === "AbortError") {
          EverTrue.Alert.error("Retry upload was cancelled by the user");
        } else {
          EverTrue.Alert.error(`Retry upload failed ${error}`);
          // Wait for a short time before retrying
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await retryUpload();
        }
      }
    };

    await retryUpload();
  }, [lastBlob, saveVideo]);

  const saveVideoToState = (videoUrl) => {
    updateState((prevState) => ({
      savedVideos: [...prevState.savedVideos, videoUrl],
    }));
  };
  const changeResolution = (newResolution) => {
    setSelectedResolution(newResolution);
  };

  const toggleSetting = (setting) => {
    setState((prevState) => {
      const selectedVideo = prevState.signalVideos.find(
        (video) => video.personalVideo.id === prevState.selectedVideoId
      );
      if (!selectedVideo) return prevState;

      return {
        ...prevState,
        signalVideos: prevState.signalVideos.map((video) =>
          video.personalVideo.id === prevState.selectedVideoId
            ? {
                ...video,
                personalVideo: {
                  ...video.personalVideo,
                  [setting]: !video.personalVideo[setting],
                },
              }
            : video
        ),
      };
    });
  };

  const updateSelectedVideo = (updatedData) => {
    setState((prevState) => ({
      ...prevState,
      signalVideos: prevState.signalVideos.map((video) =>
        video.personalVideo.id === prevState.selectedVideoId
          ? { ...video, personalVideo: { ...video.personalVideo, ...updatedData } }
          : video
      ),
    }));
  };

  const getBrowserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      EverTrue.Alert.error(`Error getting timezone ${e}`);
      return "UTC"; // Default to UTC if unable to get the timezone
    }
  };
  const handleCopy = (video, libraryVideo, styles) => {
    let url = `https://thankview.com/video/personal/${video.id_string}`;
    const convertStyleObjectToCss = (styleObject) => {
      return Object.entries(styleObject)
        .map(([key, value]) => `${key.replace(/([A-Z])/g, "-$1").toLowerCase()}:${value}`)
        .join(";");
    };

    const copyGifThumbnailWithHtml = async () => {
      try {
        const updatedLink = `${url}?type=${video.thumbnail_type === 2 ? "envelope" : "video"}&&source=export`;
        const htmlToAppend = `
          <div style="${convertStyleObjectToCss(container)}">
            <img 
              src="${libraryVideo.animated_thumb_path ? libraryVideo.animated_thumb_path : libraryVideo.video_thumb}" 
              alt="Video Thumbnail" 
              style="${convertStyleObjectToCss(imgStyle)}"
            >
            <p style="${convertStyleObjectToCss({ ...textStyle, marginBottom: "10px" })}">
              Click <a href="${updatedLink}" style="${convertStyleObjectToCss(
          linkStyle
        )}">here</a> to see your Signal Video!
            </p>
            <a 
              href="${video.button_url}" 
              target="_blank" 
              rel="noopener noreferrer" 
              style="${convertStyleObjectToCss({ ...button, display: "inline-block", marginTop: "10px" })}"
            >
              ${video.button_text.trim()}
            </a>
          </div>
        `;

        if (navigator.clipboard && window.ClipboardItem) {
          // eslint-disable-next-line no-undef
          const clipboardItem = new ClipboardItem({
            "text/html": new Blob([htmlToAppend], { type: "text/html" }),
          });
          await navigator.clipboard.write([clipboardItem]);
        } else {
          // Fallback for browsers that don't support ClipboardItem
          const tempElem = document.createElement("div");
          tempElem.innerHTML = htmlToAppend;
          document.body.appendChild(tempElem);
          const range = document.createRange();
          range.selectNodeContents(tempElem);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          document.execCommand("copy");
          document.body.removeChild(tempElem);
        }

        EverTrue.Alert.success("Video successfully copied to clipboard");
        return true;
      } catch (err) {
        EverTrue.Alert.error(`Failed to copy video to clipboard ${err}`);
      }
    };

    return copyGifThumbnailWithHtml();
  };
  const handleLibraryVideoReview = () => {
    try {
      setSelectedVideo(selectedLibraryVideo);
      reviewVideo(selectedLibraryVideo);
      updateState({
        reviewComplete: true,
      });
    } catch (error) {
      console.error(`Error reviewing video ${error}`);
      updateState({
        reviewError: error.message,
      });
      throw error;
    }
  };

  const value = {
    getBrowserTimezone,
    state,
    updateState,
    user,
    clientId,
    videoRef,
    reviewVideoRef,
    mediaRecorderRef,
    chunksRef,
    screenVideoRef,
    timestampInterval,
    startCountdown,
    startTimestamp,
    stopTimestamp,
    resetTimestamp,
    clearRecording,
    enableWebcam,
    stopWebcam,
    startRecording,
    stopRecording,
    setModalOpen,
    rerecord,
    saveVideo,
    resetRecorderState,
    saveVideoToState,
    resolutions,
    selectedResolution,
    changeResolution,
    selectedAudioDevice,
    selectedVideoDevice,
    selectedVideo,
    audioDevices,
    videoDevices,
    setSelectedAudioDevice,
    setSelectedVideoDevice,
    toggleSetting,
    updateSelectedVideo,
    color,
    trackColor,
    setColor,
    setTrackColor,
    handleColorChange,
    handleTrackColorChange,
    uploadVideoFromFile,
    handleCopy,
    handleToastClose,
    handleUpload,
    handleRetry,
    setClientId,
    setClientSecret,
    client_id,
    client_secret,
    handleLibraryVideo,
    handleCopyToast,
    setSelectedLibraryVideo,
    selectedLibraryVideo,
    setSelectedVideo,
    handleLibraryVideoReview,
  };

  return <RecorderContext.Provider value={value}>{children}</RecorderContext.Provider>;
};

RecorderProvider.propTypes = {
  children: PropTypes.any,
};

export const useRecorder = () => useContext(RecorderContext);
